import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";

const ConversationQueueNavigator = ({
  protocolId,
  position = null,
  total = null,
  canGoPrevious = false,
  canGoNext = false,
  isFetchingNextPage = false,
  inQueue = true,
  onPrevious,
  onNext,
  compact = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");

  const showPosition = inQueue && position != null && total != null && total > 0;
  const protocolLabel = protocolId
    ? t("queue.protocolShort", { id: protocolId, defaultValue: `Protocolo ${protocolId}` })
    : t("queue.protocolUnknown", "Solicitação");

  const previousTitle = t("queue.previous", "Solicitação anterior");
  const nextTitle = t("queue.next", "Próxima solicitação");
  const previousHint = t("queue.previousHint", "Seta ↑");
  const nextHint = t("queue.nextHint", "Seta ↓");
  const outOfQueueHint = t(
    "queue.outOfQueueHint",
    "Esta solicitação não está na lista filtrada atual do chat."
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 0.75 : 1,
        minWidth: 0,
        flexWrap: "nowrap",
      }}
    >
      {showPosition && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {t("queue.position", {
            current: position,
            total,
            defaultValue: `${position} de ${total}`,
          })}
        </Typography>
      )}

      <Box
        sx={{
          display: "inline-flex",
          alignItems: "stretch",
          minWidth: 0,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          overflow: "hidden",
          boxShadow:
            theme.palette.mode === "dark"
              ? "none"
              : `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Tooltip title={canGoPrevious ? `${previousTitle} · ${previousHint}` : previousTitle}>
          <span>
            <IconButton
              size="small"
              onClick={onPrevious}
              disabled={!canGoPrevious}
              aria-label={previousTitle}
              sx={{
                borderRadius: 0,
                width: 36,
                height: 36,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.25,
            minWidth: 0,
            borderLeft: `1px solid ${theme.palette.divider}`,
            borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.03)
                : alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography
            variant="body2"
            noWrap
            title={protocolLabel}
            sx={{ fontWeight: 700, minWidth: 0, maxWidth: compact ? 140 : 220 }}
          >
            {protocolLabel}
          </Typography>
        </Box>

        <Tooltip
          title={
            canGoNext
              ? `${nextTitle} · ${nextHint}${isFetchingNextPage ? ` · ${t("queue.loadingMore", "Carregando…")}` : ""}`
              : nextTitle
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => onNext?.()}
              disabled={!canGoNext || isFetchingNextPage}
              aria-label={nextTitle}
              sx={{
                borderRadius: 0,
                width: 36,
                height: 36,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!inQueue && (
        <Chip
          size="small"
          variant="outlined"
          label={outOfQueueHint}
          sx={{
            maxWidth: compact ? 180 : 260,
            height: "auto",
            "& .MuiChip-label": {
              whiteSpace: "normal",
              lineHeight: 1.25,
              py: 0.5,
            },
          }}
        />
      )}
    </Box>
  );
};

export default ConversationQueueNavigator;
